import { useRef } from 'react'
import { useAccount, useChainId, useReadContract } from 'wagmi'

/** Enable via Vercel env `NEXT_PUBLIC_DEBUG_USE_READ=true` (redeploy) or in the browser console: `localStorage.setItem('coast_debug_use_read','1')` then refresh. */
export function shouldDebugBalanceReads(): boolean {
  if (typeof window === 'undefined') return false
  if (process.env.NEXT_PUBLIC_DEBUG_USE_READ === 'true') return true
  try {
    return window.localStorage.getItem('coast_debug_use_read') === '1'
  } catch {
    return false
  }
}

type ReadParams<T> = {
  contract?: any
  abi: readonly object[]
  functionName: string
  args?: unknown[]
  validate: (value: unknown) => value is T
  watch?: boolean
  loading?: boolean
  enabled?: boolean
  account?: any
  writing?: any
}

export type ContractRead<T> = {
  value?: T
  state: 'disabled' | 'enabled' | 'loading' | 'error'
  refresh?: () => void
}

export default <T>({
  contract,
  abi,
  functionName,
  args,
  validate,
  loading = false,
  enabled = true,
  account = undefined,
  writing = undefined,
}: ReadParams<T>): ContractRead<T> => {
  const value = useRef<T | undefined>()
  const { isConnected } = useAccount()
  const isEnabled = enabled && isConnected && !loading
  const chainId = useChainId()

  const contractRead = useReadContract({
    chainId,
    query: {
      enabled: isEnabled,
      refetchInterval: writing ? 200 : 0,
    },
    account,
    address: contract,
    abi,
    functionName,
    args,
  })

  if (shouldDebugBalanceReads() && functionName === 'balanceOf') {
    // eslint-disable-next-line no-console
    console.log('[useRead balanceOf]', {
      contract,
      args,
      isConnected,
      enabledFlag: enabled,
      isEnabled,
      chainId,
      status: contractRead.status,
      fetchStatus: contractRead.fetchStatus,
      isPending: contractRead.isPending,
      isFetching: contractRead.isFetching,
      isLoading: contractRead.isLoading,
      isError: contractRead.isError,
      data: contractRead.data,
      dataType: contractRead.data === undefined ? 'undefined' : typeof contractRead.data,
      error: contractRead.error,
    })
  }

  if (!isEnabled) {
    return {
      state: 'disabled',
    }
  }

  const refresh = () => {
    let count = 0
    const maxExecutions = 25

    const intervalId = setInterval(() => {
      if (count >= maxExecutions) {
        clearInterval(intervalId)
      } else {
        if (contractRead.refetch) {
          contractRead.refetch()
        }
        count++
      }
    }, 200)
  }

  // TanStack Query v5: isLoading === isPending && isFetching. There is a window where
  // status is still "pending" but fetchStatus is "idle" (before the first request runs),
  // so isLoading is false while data is undefined — then validate(data) fails and callers
  // see no `value` and render Number(undefined) as NaN. Use isPending, not only isLoading.
  if (contractRead.isPending || contractRead.isFetching) {
    if (value.current !== undefined) {
      return {
        state: 'enabled',
        value: value.current,
        refresh,
      }
    }
    return {
      state: 'loading',
    }
  }

  if (contractRead.isError || !validate(contractRead.data)) {
    // eslint-disable-next-line no-console
    console.error('Error in contractRead', contractRead.error)
    return {
      state: 'error',
    }
  }

  value.current = contractRead.data

  return {
    state: 'enabled',
    value: contractRead.data,
    refresh,
  }
}
